import * as _ from 'lodash';
import { type VoidFunctionComponent } from 'react';

import { fetcherToPlainText } from '../../../../../../shared/fetchers';
import Locator from '../../../../../../shared/Locator';
import { DependencySource } from '../../../../../../shared/vendoredDependencies';
import { CopiableText } from '../../../../generic';
import { DetailsPanelList } from '../../../../generic/DetailsPanel';
import { DynamicShowMoreText } from '../../../../generic/DynamicShowMoreText/DynamicShowMoreText';
import FetcherIcon from '../../../../generic/FetcherIcon';
import ExternalLink from '../../../../generic/Link/ExternalLink';
import PackageLabelsList from '../../../../shared/packageLabels/PackageLabelsList';
import LicensingSection from '../../DependenciesTable/DependencyTableRow/DetailsRow/LicensingSections/LicensingSection';
import useDependencyMode from '../../hooks/useDependencyMode';
import type { Dependency } from '../../../../../../shared/dependenciesV2';
import type { Fetcher } from '../../../../../../shared/fetchers';

import BinaryConfidence from './BinaryConfidence';

import styles from '../DependencyDrawer.module.less';

type Props = {
  dependency: Dependency;
};

/**
 * Show different sections depending on which view we are in. Top-level views are are now exclusively managed
 * or vendored, so use the static route context to infer which set of sections to display.
 */
const MANAGED_SECTIONS = new Set(['Version', 'Package Manager', 'Depth', 'Locator', 'Homepage', 'Source Code', 'Origin']);
const VENDORED_SECTIONS = new Set(['Version', 'Locator', 'Homepage', 'Source Code']);

const DependencyDetails: VoidFunctionComponent<Props> = ({ dependency }) => {
  const fetcher = Locator.parse(dependency.locator).fetcher;
  // Release groups might have multiple CPEs for the same dependency, but since they (usually) match, we always
  // just show the first one.
  const cpe = dependency.cpes[0];

  // infer view mode from route path
  const mode = useDependencyMode();
  const sections = mode === DependencySource.Vendored ? VENDORED_SECTIONS : MANAGED_SECTIONS;

  return (
    <>
      {fetcher === 'csbinary' && <BinaryConfidence dependency={dependency} />}
      <div className={styles.descriptionContainer}>
        <DynamicShowMoreText text={dependency.description} />
      </div>

      <DetailsPanelList
        rows={[
          {
            label: 'Version',
            content: dependency.version ?? Locator.parse(dependency.locator).revision,
          },
          {
            label: 'Package Manager',
            content: fetcher && (
              <>
                <FetcherIcon fetcher={fetcher} />
                {fetcherToPlainText(fetcher as Fetcher)}
              </>
            ),
          },
          {
            label: 'Depth',
            content: dependency.depth > 1 ? 'Deep' : 'Direct',
          },
          {
            label: 'Locator',
            content: <CopiableText name="Locator" id="locator" value={dependency.locator} />,
          },
          {
            label: 'Homepage',
            content: _.isEmpty(dependency.url) ? 'Unknown' : <ExternalLink to={dependency.url} alignIcon="right" />,
          },
          ...(cpe ? [{
            label: 'CPE',
            content: (
              <span className="font-family-monospace">
                {cpe}
              </span>
            ),
          }] : []),
          ...(dependency.originPaths?.length > 0 ? [{
            label: 'Origin',
            content: dependency.originPaths.join(', '),
          }] : []),
          {
            label: 'Declared Licenses',
            content: _.isEmpty(dependency.declaredLicenses) ? (
              'None'
            ) : (
              <LicensingSection
                licenses={dependency.declaredLicenses}
                declaredLicenses={dependency.declaredLicenses}
                issues={dependency.issues}
              />
            ),
          },
          {
            label: 'Discovered Licenses',
            content: _.isEmpty(dependency.discoveredLicenses) ? 'None' :
            <LicensingSection
              licenses={dependency.discoveredLicenses}
              declaredLicenses={dependency.declaredLicenses}
              issues={dependency.issues} />,
          },
          {
            label: 'Package Labels',
            content: (
              _.isEmpty(dependency.labels) ? 'None' :
              <PackageLabelsList
                labels={dependency.labels}
                dependencyLocator={new Locator(dependency.locator)}
                dependencyVersion={dependency.version}
                labelPrefix=""
                enforceUnique />
            ),
          },
        ].filter((section) => sections.has(section.label))}
      />
    </>
  );
};

export default DependencyDetails;
